import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBell, FaUser, FaTasks, FaChartLine, FaCheckCircle, FaTimes, FaMapMarkerAlt, FaRegHospital, FaHardHat, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import DynamicLinkageCode from './DynamicLinkageCode';
import { getFirestore, collection, doc, getDoc, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title } from 'chart.js';
import { notificationService } from '../services/notificationService';

Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title);

interface EmployeeStats {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  totalHours: number;
  averageRating: number;
  projectsInvolved: number;
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  priority: 'low' | 'medium' | 'high';
  dueDate: string;
  assignedBy: string;
  projectId: string;
  estimatedHours: number;
  actualHours?: number;
  createdAt: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'completed' | 'on_hold';
  startDate: string;
  endDate?: string;
  progress: number;
  assignedTasks: number;
  completedTasks: number;
}

const EmployeeDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userData, setUserData] = useState<any>(null);
  const [stats, setStats] = useState<EmployeeStats>({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    totalHours: 0,
    averageRating: 0,
    projectsInvolved: 0
  });
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'unread' | 'read'>('unread');
  const [linkageModalOpen, setLinkageModalOpen] = useState(false);
  const [businessName, setBusinessName] = useState<string>('');
  const bellRef = useRef<HTMLButtonElement>(null);

  // Función para marcar notificación como leída
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markNotificationAsRead(currentUser!.uid, notificationId);
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Componente para notificaciones no leídas
  const UnreadNotifications = ({ notifications, onMarkAsRead }: { notifications: any[], onMarkAsRead: (id: string) => void }) => (
    <div className="space-y-3">
      {notifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaBell className="text-3xl mx-auto mb-2 text-gray-300" />
          <p>{t('employeeDashboard.noUnreadNotifications', 'No hay notificaciones sin leer')}</p>
        </div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onMarkAsRead(notification.id)}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {notification.type?.includes('approved') ? (
                  <FaCheckCircle className="text-green-500" />
                ) : notification.type?.includes('rejected') ? (
                  <FaExclamationTriangle className="text-red-500" />
                ) : (
                  <FaInfoCircle className="text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  {notification.title || notification.message}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(notification.createdAt).toLocaleString('es-ES')}
                </p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Componente para notificaciones leídas
  const ReadNotifications = ({ notifications }: { notifications: any[] }) => (
    <div className="space-y-3">
      {notifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaBell className="text-3xl mx-auto mb-2 text-gray-300" />
          <p>{t('employeeDashboard.noReadNotifications', 'No hay notificaciones leídas')}</p>
        </div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className="p-4 rounded-lg border border-gray-200 bg-gray-50"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {notification.type?.includes('approved') ? (
                  <FaCheckCircle className="text-green-400" />
                ) : notification.type?.includes('rejected') ? (
                  <FaExclamationTriangle className="text-red-400" />
                ) : (
                  <FaInfoCircle className="text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-700 mb-1">
                  {notification.title || notification.message}
                </h4>
                <p className="text-sm text-gray-500 mb-2">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(notification.createdAt).toLocaleString('es-ES')}
                </p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Cargar el nombre de la empresa si está asignado
          if (data.businessId) {
            try {
              const businessDoc = await getDoc(doc(db, 'businesses', data.businessId));
              if (businessDoc.exists()) {
                setBusinessName(businessDoc.data().companyName || '');
              }
            } catch (error) {
              console.error('Error fetching business name:', error);
            }
          }
        }
        setLoading(false);
              } catch (error) {
          console.error('Error fetching user data:', error);
          setError(t('employeeDashboard.error.loadingUserData', 'Error al cargar datos del usuario'));
          setLoading(false);
        }
    };

    fetchUserData();
  }, [currentUser]);

  // Fetch employee tasks
  useEffect(() => {
    if (!currentUser) return;

    const fetchTasks = async () => {
      try {
        const db = getFirestore();
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('assignedTo', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
          const tasks: Task[] = [];
          let totalHours = 0;
          let completedTasks = 0;
          let pendingTasks = 0;

          snapshot.forEach((doc) => {
            const taskData = doc.data() as Task;
            const task = { ...taskData, id: doc.id };
            tasks.push(task);

            if (task.status === 'completed') {
              completedTasks++;
              totalHours += task.actualHours || 0;
            } else if (task.status === 'pending' || task.status === 'in_progress') {
              pendingTasks++;
            }
          });

          setRecentTasks(tasks.slice(0, 5));
          setStats(prev => ({
            ...prev,
            totalTasks: tasks.length,
            completedTasks,
            pendingTasks,
            totalHours
          }));
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    fetchTasks();
  }, [currentUser]);

  // Fetch projects
  useEffect(() => {
    if (!currentUser) return;

    const fetchProjects = async () => {
      try {
        const db = getFirestore();
        const projectsQuery = query(
          collection(db, 'projects'),
          where('assignedEmployees', 'array-contains', currentUser.uid)
        );

        const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
          const projects: Project[] = [];
          
          snapshot.forEach((doc) => {
            const projectData = doc.data() as Project;
            const project = { ...projectData, id: doc.id };
            projects.push(project);
          });

          setActiveProjects(projects.filter(p => p.status === 'active'));
          setStats(prev => ({
            ...prev,
            projectsInvolved: projects.length
          }));
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, [currentUser]);

  // Fetch notifications
  useEffect(() => {
    if (!currentUser) return;

    const fetchNotifications = async () => {
      try {
        const db = getFirestore();
        const notificationsQuery = query(
          collection(db, 'notifications', currentUser.uid, 'notifications')
        );

        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
          const notifications: any[] = [];
          snapshot.forEach((doc) => {
            notifications.push({ id: doc.id, ...doc.data() });
          });
          setNotifications(notifications);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
  }, [currentUser]);

  // Update time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Chart data for task completion
  const taskCompletionData = {
    labels: [
      t('employeeDashboard.chart.completed', 'Completadas'), 
      t('employeeDashboard.chart.pending', 'Pendientes'), 
      t('employeeDashboard.chart.inProgress', 'En Progreso')
    ],
    datasets: [
      {
        data: [stats.completedTasks, stats.pendingTasks, stats.totalTasks - stats.completedTasks - stats.pendingTasks],
        backgroundColor: ['#10B981', '#F59E0B', '#3B82F6'],
        borderWidth: 2,
        borderColor: '#fff',
        hoverOffset: 8,
      },
    ],
  };

  // Chart data for productivity over time
  const productivityData = {
    labels: [
      t('employeeDashboard.chart.mon', 'Lun'), 
      t('employeeDashboard.chart.tue', 'Mar'), 
      t('employeeDashboard.chart.wed', 'Mié'), 
      t('employeeDashboard.chart.thu', 'Jue'), 
      t('employeeDashboard.chart.fri', 'Vie'), 
      t('employeeDashboard.chart.sat', 'Sáb'), 
      t('employeeDashboard.chart.sun', 'Dom')
    ],
    datasets: [
      {
        label: t('employeeDashboard.chart.workedHours', 'Horas Trabajadas'),
        data: [8, 7.5, 8.5, 6, 9, 4, 0],
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
    },
  };

  const formatDate = (date: Date) => {
    const monthsEN = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthsES = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const month = date.getMonth();
    const day = date.getDate();
    const year = date.getFullYear();
    
    return i18n.language === 'es'
      ? `${day} de ${monthsES[month]} de ${year}`
      : `${monthsEN[month]} ${day}, ${year}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(i18n.language, { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false 
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('employeeDashboard.status.completed', 'Completada');
      case 'in_progress': return t('employeeDashboard.status.inProgress', 'En Progreso');
      case 'pending': return t('employeeDashboard.status.pending', 'Pendiente');
      case 'overdue': return t('employeeDashboard.status.overdue', 'Vencida');
      default: return t('employeeDashboard.status.unknown', 'Desconocido');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return t('employeeDashboard.priority.high', 'Alta');
      case 'medium': return t('employeeDashboard.priority.medium', 'Media');
      case 'low': return t('employeeDashboard.priority.low', 'Baja');
      default: return t('employeeDashboard.priority.unknown', 'Desconocida');
    }
  };

  if (loading) {
    return (
      <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
        <div className="text-center p-8">{t('employeeDashboard.loading', 'Cargando dashboard...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
        <div className="text-red-600 text-center p-8">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
      {/* Header */}
      <header className="grid grid-cols-1 md:grid-cols-3 items-center mb-12 p-10 bg-white rounded-3xl shadow-2xl border border-white/40 w-full relative overflow-hidden gap-8 before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-1.5 before:bg-gradient-to-r before:from-red-600 before:to-pink-500 before:rounded-b-3xl before:z-10">
        <div className="relative flex items-center gap-6 justify-self-start">
          <button 
            ref={bellRef}
            onClick={() => setNotificationsModalOpen(true)} 
            aria-label={t('employeeDashboard.notifications')}
            className="bg-transparent border-none cursor-pointer text-2xl text-red-600 mr-3 flex items-center transition-colors duration-200 hover:text-pink-500 relative"
          >
            <FaBell />
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full text-xs font-bold px-1.5 py-0.5 min-w-[20px] text-center border-2 border-white shadow-lg animate-bounce">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </button>
          <div className="flex items-center gap-4">
            {/* Espacio para futuras funcionalidades */}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 text-center justify-self-center">
          <div className="flex items-center justify-center gap-2 text-xl font-medium text-gray-800 leading-tight">
            <span className="relative inline-block mr-2">
              {userData?.sector === 'health' ? (
                <FaRegHospital className="text-2xl text-red-500 drop-shadow-lg" title={t('employeeDashboard.sector.health', 'Health sector')} />
              ) : userData?.sector === 'construction' ? (
                <FaHardHat className="text-2xl text-yellow-500 drop-shadow-lg" title={t('employeeDashboard.sector.construction', 'Construction sector')} />
              ) : null}
              <span className="absolute inset-0 rounded-full shimmer-mask pointer-events-none" />
            </span>
            {t('employeeDashboard.welcome', 'Welcome')}, {userData?.firstName || t('employeeDashboard.defaultEmployeeName', 'Employee')}
            <span title={t('employeeDashboard.locationStatus', 'Estado de ubicación')}>
              <FaMapMarkerAlt className="text-red-500 text-lg" />
            </span>
          </div>
          {businessName && userData?.status === 'active' && (
            <div className="text-lg font-medium text-gray-600 leading-tight">
              {businessName}
            </div>
          )}
          <div className="text-4xl font-bold text-red-600 leading-tight">
            {formatTime(currentTime)}
          </div>
          <div className="text-xl font-medium text-gray-800 leading-tight">
            {formatDate(currentTime)}
          </div>
        </div>
        <span className="px-6 py-3 rounded-full bg-gradient-to-br from-red-600 to-pink-500 text-white font-semibold shadow-lg transform -translate-y-0.5 transition-transform duration-300 justify-self-end">
          {t('employeeDashboard.statusLabel')}: {userData?.status === 'active' ? t('employeeDashboard.active') : t('employeeDashboard.pending')}
        </span>
      </header>

      {/* Resumen superior - Solo mostrar si el usuario está asignado a una empresa */}
      {userData?.businessId && userData?.status === 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10 w-full">
          {/* Total de tareas */}
          <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
            <span className="text-3xl font-bold text-red-600 mb-1">{stats.totalTasks}</span>
            <span className="text-gray-700 font-medium text-sm text-center">{t('employeeDashboard.totalTasks', 'Total Tareas')}</span>
          </div>
          {/* Tareas completadas */}
          <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
            <span className="text-3xl font-bold text-green-600 mb-1">{stats.completedTasks}</span>
            <span className="text-gray-700 font-medium text-sm text-center">{t('employeeDashboard.completedTasks', 'Tareas Completadas')}</span>
          </div>
          {/* Horas totales */}
          <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
            <span className="text-3xl font-bold text-pink-600 mb-1">{stats.totalHours}{t('employeeDashboard.hoursSuffix', 'h')}</span>
            <span className="text-gray-700 font-medium text-sm text-center">{t('employeeDashboard.totalHours', 'Horas Totales')}</span>
          </div>
          {/* Proyectos activos */}
          <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
            <span className="text-3xl font-bold text-purple-600 mb-1">{stats.projectsInvolved}</span>
            <span className="text-gray-700 font-medium text-sm text-center">{t('employeeDashboard.activeProjects', 'Proyectos Activos')}</span>
          </div>
        </div>
      )}

      {/* Grid superior - Diferente layout según si está asignado a empresa o no */}
      {userData?.businessId && userData?.status === 'active' ? (
        // Layout completo para usuarios asignados a empresas
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8 w-full relative">
          {/* Mi perfil */}
          <section className="bg-white rounded-[20px_40px_20px_40px] p-6 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10 md:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('employeeDashboard.myProfile', 'Mi Perfil')}</h2>
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
              {currentUser?.uid && (
                <QRCodeSVG value={currentUser.uid} size={180} level="H" includeMargin={true} />
              )}
              <button
                className="mt-4 px-10 py-4 bg-gradient-to-r from-red-600 via-pink-500 to-red-600 hover:from-pink-500 hover:to-red-500 text-white rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-red-500/30 backdrop-blur-md bg-white/30 border border-white/20"
                onClick={() => navigate(`/profile`)}
              >
                {t('employeeDashboard.goToProfile', 'Ver Perfil')}
              </button>
            </div>
          </section>

          {/* Gráfico de completación de tareas */}
          <div className="bg-white rounded-[20px_40px_20px_40px] p-6 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10 md:col-span-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t('employeeDashboard.taskCompletion', 'Completación de Tareas')}
            </h3>
            <div className="h-64">
              <Doughnut data={taskCompletionData} options={chartOptions} />
            </div>
          </div>

          {/* Gráfico de productividad */}
          <div className="bg-white rounded-[20px_40px_20px_40px] p-6 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10 md:col-span-5">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t('employeeDashboard.productivity', 'Productividad Semanal')}
            </h3>
            <div className="h-64">
              <Line data={productivityData} options={chartOptions} />
            </div>
          </div>
        </div>
      ) : (
        // Layout simplificado para usuarios no asignados
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 w-full relative">
          {/* Mi perfil */}
          <section className="bg-white rounded-[20px_40px_20px_20px] p-6 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('employeeDashboard.myProfile', 'Mi Perfil')}</h2>
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
              {currentUser?.uid && (
                <QRCodeSVG value={currentUser.uid} size={180} level="H" includeMargin={true} />
              )}
              <button
                className="mt-4 px-10 py-4 bg-gradient-to-r from-red-600 via-pink-500 to-red-600 hover:from-pink-500 hover:to-red-500 text-white rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-red-500/30 backdrop-blur-md bg-white/30 border border-white/20"
                onClick={() => navigate(`/profile`)}
              >
                {t('employeeDashboard.goToProfile', 'Ver Perfil')}
              </button>
            </div>
          </section>

          {/* Sección de registro */}
          <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('employeeDashboard.getRegistered', 'Get Registered')}</h2>
            </div>
            <div className="flex flex-col items-center justify-center gap-6 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-500 via-orange-400 to-yellow-600 flex items-center justify-center text-white text-2xl font-bold shadow-md border-2 border-white/60">
                <FaUser className="text-3xl" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  {t('employeeDashboard.waitingForAssignment', 'Waiting for Company Assignment')}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('employeeDashboard.assignmentMessage', 'Your dashboard features will be enabled once a company registers you as part of their team.')}
                </p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 w-full">
                <p className="text-sm text-yellow-800">
                  {t('employeeDashboard.assignmentNote', 'Currently, you can only view your profile. Task management, project tracking, and productivity features will be available after company assignment.')}
                </p>
              </div>
              <button
                className="mt-4 px-8 py-3 bg-gradient-to-r from-yellow-500 via-orange-400 to-yellow-600 hover:from-orange-500 hover:to-yellow-500 text-white rounded-xl font-semibold text-lg shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-500/30"
                onClick={() => setLinkageModalOpen(true)}
              >
                {t('employeeDashboard.startLinkage', 'Start Linkage')}
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Grid inferior de 2 columnas - Solo mostrar si el usuario está asignado a una empresa */}
      {userData?.businessId && userData?.status === 'active' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 w-full">
          {/* Tareas recientes */}
          <div className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {t('employeeDashboard.recentTasks', 'Tareas Recientes')}
              </h3>
              <button 
                onClick={() => navigate('/employee/tasks')}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                {t('employeeDashboard.viewAll', 'Ver todas')}
              </button>
            </div>
            
            <div className="space-y-4">
              {recentTasks.length > 0 ? (
                recentTasks.map((task) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">{task.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {getStatusLabel(task.status)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {getPriorityLabel(task.priority)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>{t('employeeDashboard.dueDate', 'Vence')}: {new Date(task.dueDate).toLocaleDateString()}</p>
                        <p>{task.estimatedHours}{t('employeeDashboard.hoursSuffix', 'h')} {t('employeeDashboard.estimated', 'estimadas')}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaTasks className="text-3xl mx-auto mb-2 text-gray-300" />
                  <p>{t('employeeDashboard.noTasks', 'No hay tareas asignadas')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Proyectos activos */}
          <div className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {t('employeeDashboard.activeProjects', 'Proyectos Activos')}
              </h3>
              <button 
                onClick={() => navigate('/employee/projects')}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                {t('employeeDashboard.viewAll', 'Ver todos')}
              </button>
            </div>
            
            <div className="space-y-4">
              {activeProjects.length > 0 ? (
                activeProjects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">{project.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex justify-between text-sm text-gray-500 mb-1">
                              <span>{t('employeeDashboard.progress', 'Progreso')}</span>
                              <span>{project.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${project.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span>{project.completedTasks}/{project.assignedTasks} {t('employeeDashboard.tasks', 'tareas')}</span>
                          <span>{t('employeeDashboard.starts', 'Inicia')}: {new Date(project.startDate).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaChartLine className="text-3xl mx-auto mb-2 text-gray-300" />
                  <p>{t('employeeDashboard.noProjects', 'No hay proyectos activos')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificaciones */}
      {notificationsModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">{t('employeeDashboard.notificationsTitle', 'Notificaciones')}</h2>
              <button
                onClick={() => setNotificationsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <FaTimes />
              </button>
            </div>

            {/* Pestañas */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'unread'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('employeeDashboard.unreadTab', 'Sin leer')} ({notifications.filter(n => !n.read).length})
              </button>
              <button
                onClick={() => setActiveTab('read')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'read'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('employeeDashboard.readTab', 'Leídas')} ({notifications.filter(n => n.read).length})
              </button>
            </div>

            {/* Contenido de las pestañas */}
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {activeTab === 'unread' ? (
                <UnreadNotifications 
                  notifications={notifications.filter(n => !n.read)}
                  onMarkAsRead={handleMarkAsRead}
                />
              ) : (
                <ReadNotifications 
                  notifications={notifications.filter(n => n.read)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Vinculación */}
      {linkageModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">{t('employeeDashboard.linkageModal.title', 'Dynamic Linkage Code')}</h2>
              <button
                onClick={() => setLinkageModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <FaTimes />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              <div className="mb-6 text-center">
                <p className="text-gray-600 mb-4">
                  {t('employeeDashboard.linkageModal.description', 'Show this dynamic code to your company to establish the connection.')}
                </p>
                
                {/* Código Dinámico */}
                {currentUser?.uid && (
                  <div className="flex justify-center">
                    <DynamicLinkageCode 
                      userId={currentUser.uid}
                      onCodeGenerated={(code) => console.log('Generated code:', code)}
                    />
                  </div>
                )}
              </div>

              {/* Instrucciones */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">
                  {t('employeeDashboard.linkageModal.instructions', 'Instructions:')}
                </h4>
                <ol className="text-sm text-blue-700 space-y-1">
                  <li>1. {t('employeeDashboard.linkageModal.step1', 'Show this code to your company administrator')}</li>
                  <li>2. {t('employeeDashboard.linkageModal.step2', 'The company will scan your dynamic code')}</li>
                  <li>3. {t('employeeDashboard.linkageModal.step3', 'Once scanned, you will be automatically assigned to the company')}</li>
                  <li>4. {t('employeeDashboard.linkageModal.step4', 'Your dashboard will be updated with company features')}</li>
                </ol>
              </div>

              {/* Botón de cerrar */}
              <div className="flex justify-center">
                <button
                  onClick={() => setLinkageModalOpen(false)}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-pink-500 text-white rounded-lg font-medium hover:from-red-700 hover:to-pink-600 transition-colors"
                >
                  {t('employeeDashboard.linkageModal.close', 'Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard; 